import { type BuildQuizListItem } from '../form/schema/BuildQuizListSchema';
import { hasQuizItemContent } from '../form/schema/quiz/quizItemContent';
import { MIN_MATCH_PAIR_COUNT } from '../form/schema/quiz/QuizMatchDataSchema';
import { hasChoiceContent } from '../form/schema/quiz/QuizMultipleDataSchema';
import { MIN_ORDER_ITEMS } from '../form/schema/quiz/QuizOrderDataSchema';
import { hasSubjectiveAnswerContent } from '../form/schema/quiz/QuizSubjectiveDataSchema';

/** 편집 UI 가 삭제 버튼을 감추는 하한과 같다(EditableSubjective·EditableMultiple). */
const MIN_SUBJECTIVE_ANSWER_COUNT = 1;
const MIN_MULTIPLE_CHOICE_COUNT = 2;

type QuizType = BuildQuizListItem['type'];
type MultipleChoice = BuildQuizListItem['multipleData']['choiceList'][number];
type MatchData = BuildQuizListItem['matchData'];

export type QuizArrayWrite =
  | {
      path: 'subjectiveData.answerList';
      value: BuildQuizListItem['subjectiveData']['answerList'];
    }
  | { path: 'multipleData.choiceList'; value: MultipleChoice[] }
  | {
      path: 'orderData.items';
      value: NonNullable<BuildQuizListItem['orderData']>['items'];
    }
  | { path: 'matchData.leftItems'; value: MatchData['leftItems'] }
  | { path: 'matchData.rightItems'; value: MatchData['rightItems'] }
  | { path: 'matchData.answerPairs'; value: MatchData['answerPairs'] };

export type EmptyAnswerInputCleanup = {
  quizIndex: number;
  quizType: QuizType;
  removedCount: number;
  writes: QuizArrayWrite[];
};

export type EmptyAnswerInputSkipReason =
  | 'below_min'
  | 'answer_choice_empty'
  | 'match_half_filled';

export type EmptyAnswerInputSkip = {
  quizIndex: number;
  reason: EmptyAnswerInputSkipReason;
};

type CleanupPlan = {
  cleanup?: Pick<EmptyAnswerInputCleanup, 'removedCount' | 'writes'>;
  skipReason?: EmptyAnswerInputSkipReason;
};

type EmptyInputCleaner = (
  quiz: BuildQuizListItem,
  isPending: (id: string) => boolean,
) => CleanupPlan;

const listCleaner =
  <T>({
    select,
    idOf,
    hasContent,
    min,
    write,
    blockedBy,
  }: {
    select: (quiz: BuildQuizListItem) => T[];
    idOf: (item: T) => string;
    hasContent: (item: T) => boolean;
    min: number;
    write: (kept: T[]) => QuizArrayWrite;
    blockedBy?: (list: T[]) => EmptyAnswerInputSkipReason | undefined;
  }): EmptyInputCleaner =>
  (quiz, isPending) => {
    const list = select(quiz);
    const blockedReason = blockedBy?.(list);
    if (blockedReason) return { skipReason: blockedReason };

    const kept = list.filter(item => isPending(idOf(item)) || hasContent(item));
    if (kept.length === list.length) return {};
    if (kept.length < min) return { skipReason: 'below_min' };

    return {
      cleanup: {
        writes: [write(kept)],
        removedCount: list.length - kept.length,
      },
    };
  };

const matchRowCleaner: EmptyInputCleaner = (quiz, isPending) => {
  const { leftItems, rightItems, answerPairs } = quiz.matchData;
  const leftById = new Map(leftItems.map(item => [item.id, item]));
  const rightById = new Map(rightItems.map(item => [item.id, item]));
  const isFilled = (item: MatchData['leftItems'][number]) =>
    isPending(item.id) || hasQuizItemContent(item);

  const pairs = answerPairs.flatMap(pair => {
    const left = leftById.get(pair.leftId);
    const right = rightById.get(pair.rightId);
    return left && right
      ? [{ pair, hasLeft: isFilled(left), hasRight: isFilled(right) }]
      : [];
  });

  if (pairs.length !== leftItems.length || pairs.length !== rightItems.length) {
    return {};
  }

  const keptPairs = pairs.filter(
    ({ hasLeft, hasRight }) => hasLeft || hasRight,
  );
  const skipReason = keptPairs.some(
    ({ hasLeft, hasRight }) => !hasLeft || !hasRight,
  )
    ? 'match_half_filled'
    : undefined;

  if (keptPairs.length === pairs.length) return { skipReason };
  if (keptPairs.length < MIN_MATCH_PAIR_COUNT)
    return { skipReason: 'below_min' };

  const keptLeftIds = new Set(keptPairs.map(({ pair }) => pair.leftId));
  const keptRightIds = new Set(keptPairs.map(({ pair }) => pair.rightId));

  return {
    skipReason,
    cleanup: {
      removedCount: pairs.length - keptPairs.length,
      writes: [
        {
          path: 'matchData.leftItems',
          value: leftItems.filter(({ id }) => keptLeftIds.has(id)),
        },
        {
          path: 'matchData.rightItems',
          value: rightItems.filter(({ id }) => keptRightIds.has(id)),
        },
        {
          path: 'matchData.answerPairs',
          value: keptPairs.map(({ pair }) => pair),
        },
      ],
    },
  };
};

const EMPTY_INPUT_CLEANERS: Partial<Record<QuizType, EmptyInputCleaner>> = {
  subjective: listCleaner({
    select: quiz => quiz.subjectiveData.answerList,
    idOf: answer => answer.key,
    hasContent: hasSubjectiveAnswerContent,
    min: MIN_SUBJECTIVE_ANSWER_COUNT,
    write: value => ({ path: 'subjectiveData.answerList', value }),
  }),
  multiple: listCleaner({
    select: quiz => quiz.multipleData.choiceList,
    idOf: choice => choice.key,
    hasContent: hasChoiceContent,
    min: MIN_MULTIPLE_CHOICE_COUNT,
    write: value => ({ path: 'multipleData.choiceList', value }),
    // 정답으로 고른 보기가 비어 있으면 지우지 않고 검증에서 막아 직접 채우게 한다.
    blockedBy: choices =>
      choices.some(choice => choice.isAnswer && !hasChoiceContent(choice))
        ? 'answer_choice_empty'
        : undefined,
  }),
  order: listCleaner({
    select: quiz => quiz.orderData?.items ?? [],
    idOf: item => item.id,
    hasContent: hasQuizItemContent,
    min: MIN_ORDER_ITEMS,
    write: value => ({ path: 'orderData.items', value }),
  }),
  match: matchRowCleaner,
};

export const getEmptyAnswerInputCleanups = (
  quizList: BuildQuizListItem[],
  { pendingItemIds = new Set() }: { pendingItemIds?: ReadonlySet<string> } = {},
): { cleanups: EmptyAnswerInputCleanup[]; skips: EmptyAnswerInputSkip[] } => {
  const isPending = (id: string) => pendingItemIds.has(id);
  const plans = quizList.map((quiz, quizIndex) => ({
    quizIndex,
    quizType: quiz.type,
    ...EMPTY_INPUT_CLEANERS[quiz.type]?.(quiz, isPending),
  }));

  return {
    cleanups: plans.flatMap(({ quizIndex, quizType, cleanup }) =>
      cleanup ? [{ quizIndex, quizType, ...cleanup }] : [],
    ),
    skips: plans.flatMap(({ quizIndex, skipReason }) =>
      skipReason ? [{ quizIndex, reason: skipReason }] : [],
    ),
  };
};
